/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';

import { Flex, Item, TabList, TabPanels, Tabs, Provider, lightTheme } from '@adobe/react-spectrum';

import ExperienceFragmentReferenceTable from './ExperienceFragmentReferenceTable';
import ContentFragmentReferenceTable from './ContentFragmentReferenceTable';
import AssetReferenceTable from './AssetReferenceTable';
import PageReferenceTable from './PageReferenceTable';

import classes from './ContentReferenceTab.scss';

const messages = defineMessages({
    tabExperienceFragments: {
        id: 'tab.experienceFragments',
        defaultMessage: 'Experience Fragments'
    },
    tabContentFragments: {
        id: 'tab.contentFragments',
        defaultMessage: 'Content Fragments'
    },
    tabAssets: {
        id: 'tab.assets',
        defaultMessage: 'Assets'
    },
    tabPages: {
        id: 'tab.pages',
        defaultMessage: 'Pages'
    }
});

/**
 * Displays a tab view of reference tables of different content types for a given product or category.
 */
const ContentReferenceTab = props => {
    const { type, identifier } = props;
    const { formatMessage } = useIntl();

    return (
        <Provider theme={lightTheme} UNSAFE_className={classes.provider}>
            <Tabs aria-label="Content References Tabs">
                <Flex margin="size-200">
                    <TabList flex="1 1 auto" minWidth="0px">
                        <Item key="ExperienceFragments">{formatMessage(messages.tabExperienceFragments)}</Item>
                        <Item key="ContentFragments">{formatMessage(messages.tabContentFragments)}</Item>
                        {type === 'product' && <Item key="Assets">{formatMessage(messages.tabAssets)}</Item>}
                        <Item key="Pages">{formatMessage(messages.tabPages)}</Item>
                    </TabList>
                    {/* UX Placeholder
                <View flex="0 0 auto" borderBottomWidth="thin" borderColor="mid">
                    <ActionButton>
                        <Add />
                        <Text>Add Content Reference</Text>
                    </ActionButton>
                </View> */}
                </Flex>
                <TabPanels UNSAFE_className={classes.tabPanel}>
                    <Item key="ExperienceFragments">
                        <ExperienceFragmentReferenceTable type={type} identifier={identifier} />
                    </Item>
                    <Item key="ContentFragments">
                        <ContentFragmentReferenceTable type={type} identifier={identifier} />
                    </Item>
                    {type === 'product' && (
                        <Item key="Assets">
                            <AssetReferenceTable type={type} identifier={identifier} />
                        </Item>
                    )}
                    <Item key="Pages">
                        <PageReferenceTable type={type} identifier={identifier} />
                    </Item>
                </TabPanels>
            </Tabs>
        </Provider>
    );
};

ContentReferenceTab.propTypes = {
    type: PropTypes.oneOf(['product', 'category']),
    identifier: PropTypes.string
};

export default ContentReferenceTab;
